import { useEffect, useState, useMemo } from '@max/max';
import { useMonitorEngineContext } from '@nibfe/mcp-monitor-sdk';
import { useRequest } from './useRequest';
import { ActivityStatus, API_PATH as API, API_PATH, RecordTabType } from '../types/shared';
import { resolveActivityViewModel, resolveFreeCard } from '../utils/resolve';
import { getRequestBaseParams } from '../utils/request';
import { useAssistAction } from './useAssistAction';
import { useQueryPopupCache } from './useQueryPopupCache';
import { useJoinActivityAction } from './useJoinActivityAction';
const isWeb = true;
const isMRN = false;
const CITY_EXPLORE_SCENE = 1;
const IS_SHOW_COUPON_POPUP_CACHE_KEY = 'isShowCouponPopup';
const IS_SHOW_FREE_ORDER_POPUP_CACHE_KEY = 'isShowFreeOrderPopup';
const CACHE_EXPIRE_TIME = 15552000; // 15,552,000 ms = 180 days
export var PopupKey;
(function (PopupKey) {
    PopupKey["CITY_SELECT_MODAL"] = "citySelectModal";
    PopupKey["GUEST_ASSIST_POPUP"] = "guestAssistPopup";
    PopupKey["GET_FREE_ORDER_POPUP"] = "getFreeOrderPopup";
    PopupKey["GET_COUPON_POPUP"] = "getCouponPopup";
    PopupKey["LANDMARK_DETAIL_MODAL"] = "landmarkDetailModal";
    PopupKey["REWARD_RECORDS"] = "rewardRecords";
})(PopupKey || (PopupKey = {}));
const defaultPopupMap = {
    [PopupKey.CITY_SELECT_MODAL]: false,
    [PopupKey.GUEST_ASSIST_POPUP]: false,
    [PopupKey.GET_FREE_ORDER_POPUP]: false,
    [PopupKey.GET_COUPON_POPUP]: false,
    [PopupKey.LANDMARK_DETAIL_MODAL]: false,
    [PopupKey.REWARD_RECORDS]: false
};
export function checkIsHaveFreeCard(beRes) {
    try {
        const freeOrder = resolveFreeCard(beRes);
        return !!freeOrder;
    }
    catch (error) {
        return false;
    }
}
export function checkIsHaveCoupon(beRes) {
    try {
        if (Array.isArray(beRes?.rewardItems) && beRes.rewardItems.length > 0) {
            return true;
        }
        return false;
    }
    catch (error) {
        return false;
    }
}
export function checkIsJoined(beRes) {
    const selectedGroups = beRes?.moduleInfoList?.[0]?.cardModuleInfoVO?.selectedCardGroups || [];
    return Array.isArray(selectedGroups) && selectedGroups.length > 0;
}
export function getInitActivityStatus(queryInfoRes, queryRewardsRes) {
    const isJoined = checkIsJoined(queryInfoRes);
    const isHaveFreeCard = checkIsHaveFreeCard(queryRewardsRes);
    if (!isJoined) {
        return ActivityStatus.UNJOINED;
    }
    else if (isHaveFreeCard) {
        return ActivityStatus.HAVE_FREE_CARD;
    }
    return ActivityStatus.ON_PROGRESS;
}
export function useCityExploreData(options) {
    const ctx = useMonitorEngineContext();
    const commonParams = ctx._runtimeInfo?.commonParams ?? {};
    const { leaderId } = ctx._runtimeInfo?.queryParams || {};
    const { onTriggerAssistAction } = useAssistAction({
        feConfig: options.feConfig,
        activityId: options.activityId,
        taskId: options.taskId
    });
    const { checkPopupCache } = useQueryPopupCache({
        activityId: options.activityId
    });
    const { onTriggerJoinActivity } = useJoinActivityAction({
        activityId: options.activityId
    });
    const [activityStatus, setActivityStatus] = useState(ActivityStatus.LOADING);
    const [rawQueryInfoResponse, setRawQueryInfoResponse] = useState(null);
    const [rawQueryRewardsResponse, setRawQueryRewardsResponse] = useState(null);
    const [popupMap, setPopupMap] = useState(defaultPopupMap);
    const [selectedLandmark, setSelectedLandmark] = useState(null);
    const [selectedRewardTabType, setSelectedRewardTabType] = useState(RecordTabType.REWARD);
    const { requestStatus: queryInfoStatus, request: queryInfo } = useRequest(API.QUERY_INFO);
    const { requestStatus: queryRewardsStatus, request: queryRewards } = useRequest(API_PATH.REWARDS);
    const { request: queryPopupCache } = useRequest(API_PATH.QUERY_POPUP_CACHE);
    async function getInitPopupMap(isGuest, queryInfoRes, queryRewardsRes, assistRes) {
        const isJoined = checkIsJoined(queryInfoRes);
        const isHaveFreeCard = checkIsHaveFreeCard(queryRewardsRes);
        const isHaveCoupon = checkIsHaveCoupon(queryRewardsRes);
        if (isGuest && assistRes) {
            return {
                ...defaultPopupMap,
                [PopupKey.GUEST_ASSIST_POPUP]: true
            };
        }
        else if (!isJoined) {
            return {
                ...defaultPopupMap,
                [PopupKey.CITY_SELECT_MODAL]: true
            };
        }
        else if (isHaveCoupon || isHaveFreeCard) {
            const popupCacheRes = await checkPopupCache(true, {
                [IS_SHOW_COUPON_POPUP_CACHE_KEY]: CACHE_EXPIRE_TIME,
                [IS_SHOW_FREE_ORDER_POPUP_CACHE_KEY]: CACHE_EXPIRE_TIME
            });
            if (popupCacheRes) {
                if (isHaveFreeCard && !popupCacheRes?.[IS_SHOW_FREE_ORDER_POPUP_CACHE_KEY]) {
                    await checkPopupCache(false, {
                        [IS_SHOW_COUPON_POPUP_CACHE_KEY]: CACHE_EXPIRE_TIME,
                        [IS_SHOW_FREE_ORDER_POPUP_CACHE_KEY]: CACHE_EXPIRE_TIME
                    });
                    return {
                        ...defaultPopupMap,
                        [PopupKey.GET_FREE_ORDER_POPUP]: true
                    };
                }
                else if (isHaveCoupon && !popupCacheRes?.[IS_SHOW_COUPON_POPUP_CACHE_KEY]) {
                    await checkPopupCache(false, {
                        [IS_SHOW_COUPON_POPUP_CACHE_KEY]: CACHE_EXPIRE_TIME,
                        [IS_SHOW_FREE_ORDER_POPUP_CACHE_KEY]: CACHE_EXPIRE_TIME
                    });
                    return {
                        ...defaultPopupMap,
                        [PopupKey.GET_COUPON_POPUP]: true
                    };
                }
            }
        }
        return defaultPopupMap;
    }
    async function init() {
        try {
            const isGuestMode = !!leaderId;
            const feCommonParams = await getRequestBaseParams(commonParams);
            const queryInfoParams = {
                activityId: options.activityId,
                scene: CITY_EXPLORE_SCENE,
                ...feCommonParams
            };
            const queryRewardsParams = {
                activityId: options.activityId,
                ...feCommonParams
            };
            const [queryInfoRes, queryRewardsRes, queryAssistRes] = await Promise.all([queryInfo(queryInfoParams), queryRewards(queryRewardsParams).catch(() => {
                    return null;
                }), new Promise(resolve => {
                    if (isGuestMode) {
                        onTriggerAssistAction().then(data => {
                            resolve(data);
                        }).catch(() => {
                            resolve(null);
                        });
                    }
                    else {
                        resolve(null);
                    }
                })]);
            setRawQueryInfoResponse(queryInfoRes);
            setRawQueryRewardsResponse(queryRewardsRes);
            const iniPopupMap = await getInitPopupMap(isGuestMode, queryInfoRes, queryRewardsRes, queryAssistRes);
            setPopupMap(iniPopupMap);
            const initActivityStatus = getInitActivityStatus(queryInfoRes, queryRewardsRes);
            setActivityStatus(initActivityStatus);
        }
        catch (error) {
            setActivityStatus(ActivityStatus.FAILED);
            ctx._uiInteractionAPI.toast.show({
                content: '获取数据失败，请稍后重试'
            });
            ctx._raptorReport.monitorReportBizFailed?.({
                apiPath: API.QUERY_INFO
            });
            ctx._raptorReport.monitorReportLog(error.message, error, {
                category: 'ajaxError',
                level: 'error'
            });
        }
    }
    async function handleSelectCity(cityInfo) {
        try {
            const joinActivityRes = await onTriggerJoinActivity(cityInfo?.id?.toString() ?? '');
            const feCommonParams = await getRequestBaseParams(commonParams);
            const queryInfoParams = {
                activityId: options.activityId,
                scene: CITY_EXPLORE_SCENE,
                ...feCommonParams
            };
            const queryInfoRes = await queryInfo(queryInfoParams);
            setRawQueryInfoResponse(queryInfoRes);
            const initActivityStatus = getInitActivityStatus(queryInfoRes, null);
            setActivityStatus(initActivityStatus);
            setPopupMap(defaultPopupMap);
        }
        catch (error) {
            setPopupMap(defaultPopupMap);
            ctx._uiInteractionAPI.toast.show({
                content: '参与活动失败，请稍后重试'
            });
            ctx._raptorReport.monitorReportBizFailed?.({
                apiPath: API_PATH.JOIN_ACTIVITY
            });
            ctx._raptorReport.monitorReportLog?.('joinActivity', error, {
                level: 'error',
                category: 'ajaxError'
            });
        }
    }
    function handleCloseAssistModal() {
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.GUEST_ASSIST_POPUP]: false,
            [PopupKey.CITY_SELECT_MODAL]: activityStatus === ActivityStatus.UNJOINED
        });
    }
    function handleClickLandmarkItem(landmarkVo) {
        setSelectedLandmark(landmarkVo);
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.LANDMARK_DETAIL_MODAL]: true
        });
        // 发送三个事件，参照 trip-around-recommend-guide 的实现
        ctx?._communication?.emit?.('travelAroundGuideChange', {
            playName: landmarkVo.name || '',
            playUnifyIdString: landmarkVo.cardId || '',
            productInfoList: [],
            cityId: rawQueryInfoResponse?.moduleInfoList?.[0]?.cardModuleInfoVO?.selectedCardGroups?.[0] || 0
        });
        ctx?._communication?.emit?.('dynamicPoiListSearchParamsChanged', {
            landmark: landmarkVo.name || '',
            cityId: rawQueryInfoResponse?.moduleInfoList?.[0]?.cardModuleInfoVO?.selectedCardGroups?.[0] || 0,
            lat: landmarkVo.cardExtra?.latitude,
            lng: landmarkVo.cardExtra?.longitude
        });
        ctx?._communication?.emit?.('cityChanged', {
            value: rawQueryInfoResponse?.moduleInfoList?.[0]?.cardModuleInfoVO?.selectedCardGroups?.[0] || 0,
            title: rawQueryInfoResponse?.moduleInfoList?.[0]?.cardModuleInfoVO?.cardGroupInfos?.[0]?.groupName || ''
        });
    }
    function handleCloseLandmarkDetailModal() {
        setSelectedLandmark(null);
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.LANDMARK_DETAIL_MODAL]: false
        });
    }
    function handleCloseCouponModal() {
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.GET_COUPON_POPUP]: false
        });
    }
    function handleCloseRewardRecords() {
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.REWARD_RECORDS]: false
        });
    }
    function handleSelectRewardTabType(tabType) {
        setSelectedRewardTabType(tabType);
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.REWARD_RECORDS]: true
        });
    }
    function handleCloseFreeCardModal() {
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.GET_FREE_ORDER_POPUP]: false
        });
    }
    function handleCloseCityModal() {
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.CITY_SELECT_MODAL]: false
        });
    }
    function handleJoinActivity() {
        setPopupMap({
            ...defaultPopupMap,
            [PopupKey.CITY_SELECT_MODAL]: true
        });
    }
    const viewModel = useMemo(() => {
        return resolveActivityViewModel(rawQueryInfoResponse);
    }, [rawQueryInfoResponse]);
    const freeOrder = useMemo(() => {
        return resolveFreeCard(rawQueryRewardsResponse);
    }, [rawQueryRewardsResponse]);
    useEffect(() => {
        try {
            init();
        }
        catch (error) {
            ctx._uiInteractionAPI.toast.show({
                content: '获取数据失败，请稍后重试'
            });
            ctx._raptorReport.monitorReportBizFailed?.({
                apiPath: API.QUERY_INFO
            });
            ctx._raptorReport.monitorReportLog(error.message, error, {
                category: 'ajaxError',
                level: 'error'
            });
        }
    }, []);
    let showListener;
    //这个函数监听和取消监听的需要一致
    let showRes = () => {
        init();
    };
    const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            // 页面进入后台，暂停动画
            init();
        }
    };
    // 监听页面可见性变化（仅Web环境）
    useEffect(() => {
        if (true) {
            document.addEventListener('visibilitychange', handleVisibilityChange);
        }
        return () => {
            if (true) {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
            }
        };
    }, []);
    return {
        activityStatus,
        viewModel,
        rawQueryInfoResponse,
        rawQueryRewardsResponse,
        popupMap,
        selectedLandmark,
        selectedRewardTabType,
        freeOrder,
        handleSelectRewardTabType,
        handleSelectCity,
        handleCloseAssistModal,
        handleClickLandmarkItem,
        handleCloseLandmarkDetailModal,
        handleCloseCouponModal,
        handleCloseRewardRecords,
        handleCloseFreeCardModal,
        handleCloseCityModal,
        handleJoinActivity
    };
}
